import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register the PWA service worker (production only — dev server doesn't ship the file properly)
//
// Update strategy tuned for iOS Safari (which is aggressive about caching SW files):
//   1. On every load, force `reg.update()` with an updateViaCache:"none" registration
//      so iOS is *forced* to revalidate sw.js against the network.
//   2. Also poll `reg.update()` every 60s while the tab is visible + every time
//      the tab regains focus. iOS PWAs often stay "open" for days.
//   3. When a new worker installs, postMessage SKIP_WAITING → activates immediately.
//   4. On controllerchange, hard-reload once so the new HTML/JS is picked up.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((reg) => {
        // Force an update check every time the app opens.
        reg.update().catch(() => {});

        const promptReload = (worker) => {
          if (!worker) return;
          worker.addEventListener("statechange", () => {
            if (worker.state === "installed" && navigator.serviceWorker.controller) {
              // A new SW is ready and there's already a controller — activate now.
              worker.postMessage({ type: "SKIP_WAITING" });
            }
          });
        };

        // If a new SW is already waiting when we register, kick it immediately.
        if (reg.waiting) promptReload(reg.waiting);
        // Or if one gets installed during this session, prompt reload.
        reg.addEventListener("updatefound", () => promptReload(reg.installing));

        // Poll for updates every 60s so long-lived iOS PWAs don't get stuck.
        const poll = () => reg.update().catch(() => {});
        setInterval(poll, 60_000);
        // Also check whenever the tab regains focus (returning from background).
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") poll();
        });
      })
      .catch((e) => {
        console.warn("SW registration failed:", e);
      });

    // When the new SW takes control, hard reload once so the page picks up new HTML/JS.
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}
